<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Test Package Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <basic-heading :title="formattedName" class="mb-3">
      <template #right-content>
        <header-button-group :button_config="button_config">
          <template #post_buttons>
            <button
              v-show="checkPermission('delete test package')"
              title="Delete test package"
              class="btn btn-danger ml-1"
              @click="showDeleteModal"
            >
              <i class="fal fa-trash" />
            </button>
          </template>
        </header-button-group>
      </template>
    </basic-heading>
    <permission-alert-gate :permissions="'read test package'">
      <template #gated-content>
        <test-package-card :test_package="test_package" />
        <test-package-detail-cardstack :test_package="test_package" />
      </template>
    </permission-alert-gate>
  </div>
</template>

<script>
import Vue from "vue";
import VueNotifications from "vue-notifications";
import BasicHeading from "@/components/shared/BasicHeading";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import PermissionAlertGate from "@/components/permission/PermissionAlertGate";
import TestPackageCard from "@/components/lab/testpackage/TestPackageCard";
import TestPackageDetailCardstack from "@/components/lab/testpackage/TestPackageDetailCardstack";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { getModel } from "@/shared/fetch-utils";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import { notificationOptions } from "@/shared/toast-notification-options";
import { testPackageNotifications } from "@/shared/notifications";

Vue.use(VueNotifications, notificationOptions);

const TestPackageRepository = LabRepositoryFactory.get("testPackage");

export default {
  name: "TestPackageDetail",
  components: {
    BasicHeading,
    HeaderButtonGroup,
    PermissionAlertGate,
    TestPackageCard,
    TestPackageDetailCardstack,
  },
  data() {
    return {
      loading: true,
      test_package_id: this.$route.params.test_package_id || null,
      test_package: null,
    };
  },
  computed: {
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-list",
            linkto: "/testpackages",
            title: "Test package list",
          },
          {
            icon: "fa-pencil",
            linkto: `/testpackages/${this.test_package_id}/edit`,
            title: "Edit test package",
            variant: "warning",
            permission: this.checkPermission("edit test package"),
          },
        ],
      };
    },
    formattedName() {
      return `${this.test_package.sample_type.name} ${this.test_package.name}`;
    },
  },
  methods: {
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    showDeleteModal() {
      this.$bvModal
        .msgBoxConfirm(
          "You are about to remove this test package. Do you want to continue?",
          {
            title: "Are you sure?",
            modalClass: ["modal-danger"],
            size: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            centered: true,
          }
        )
        .then(async (confirm) => {
          if (confirm) {
            try {
              await TestPackageRepository.suspend(this.test_package_id);

              this.deleteSuccess();
              this.$router.push("/testpackages");
            } catch ({ response }) {
              const { status, data } = response.response;

              this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
                title: "Server Error.",
                variant: "danger",
              });
            }
          }
        });
    },
  },
  async created() {
    this.loading = true;

    this.test_package = await getModel({
      model_type: "test_package",
      id: this.test_package_id,
      message: this.$bvToast,
    });

    this.test_package ? (this.loading = false) : this.$router.back();
  },
  notifications: testPackageNotifications,
};
</script>
